// ────────────────────────────────────────────────────────────────────────────
// Evaluation Rubric definitions (versioned, code-owned contract)
//
// Source of truth for: self-review prompt, directive-analysis prompt,
// description-quality prompt, and the directive detail / skill improvement
// settings UI sections.
//
// Spec: docs/technical/evaluation-rubric-externalisation-spec.md
// Domain mirror: apps/api/src/directives/rubrics/index.ts (re-exports)
//
// IMPORTANT — versioning policy:
//   v1 rubrics are pinned. Any change to dimension ids, names, descriptions,
//   or outputKeys constitutes a behavioural change to LLM evaluation and MUST
//   ship as a NEW rubric (e.g. SELF_REVIEW_RUBRIC_V2) rather than mutating
//   an existing constant. The contract tests in
//   `packages/shared-types/src/__tests__/rubrics-contract.spec.ts` enforce
//   this.
// ────────────────────────────────────────────────────────────────────────────

/** A single judging dimension within a rubric. */
export interface RubricDimension {
  /** Stable snake_case identifier — safe to persist or analytics-tag. */
  id: string;
  /** Human-readable, sentence-case label. */
  name: string;
  /** One-sentence description of what the LLM is judging. */
  description: string;
  /** JSON output keys this dimension maps to in the LLM response. */
  outputKeys: string[];
}

/** A versioned evaluation rubric. */
export interface Rubric {
  /** Stable kebab-case slug ending in `-v{N}`. Persisted on directives. */
  slug: string;
  /** Human-readable rubric name (used in UI chips). */
  name: string;
  /** Version string, e.g. `"v1"`. */
  version: string;
  /** Ordered list of dimensions evaluated by this rubric. */
  dimensions: RubricDimension[];
}

// ── Self-Review Rubric v1 ───────────────────────────────────────────────────

export const SELF_REVIEW_RUBRIC_V1: Rubric = {
  slug: 'self-review-v1',
  name: 'Self-Review Rubric v1',
  version: 'v1',
  dimensions: [
    {
      id: 'description_quality',
      name: 'Description quality',
      description: 'Is the skill description clear, complete, and actionable?',
      outputKeys: ['suggestions[].type=description_improvement'],
    },
    {
      id: 'feedback_trend_analysis',
      name: 'Feedback trend analysis',
      description: 'Are ratings improving, declining, or stable over time?',
      outputKeys: [
        'feedbackAnalysis.commonThemes',
        'feedbackAnalysis.negativePatterns',
        'feedbackAnalysis.positivePatterns',
      ],
    },
    {
      id: 'coverage_gaps',
      name: 'Coverage gaps',
      description: 'Do feedback comments suggest capabilities not reflected in the description?',
      outputKeys: ['suggestions[].type=coverage_gap'],
    },
    {
      id: 'staleness_indicators',
      name: 'Staleness indicators',
      description: 'Does the description seem current given recent feedback themes?',
      outputKeys: ['suggestions[].type=staleness_update'],
    },
    {
      id: 'agent_specific_performance',
      name: 'Agent-specific performance',
      description: 'How do different agents perform with this skill?',
      outputKeys: ['suggestions[].type=performance_gap'],
    },
  ],
};

// ── Directive-Analysis (Feedback Pattern) Rubric v1 ─────────────────────────

export const DIRECTIVE_ANALYSIS_RUBRIC_V1: Rubric = {
  slug: 'directive-analysis-v1',
  name: 'Feedback Analysis Rubric v1',
  version: 'v1',
  dimensions: [
    {
      id: 'common_themes',
      name: 'Common themes',
      description: 'Recurring topics across feedback comments.',
      outputKeys: ['feedbackAnalysis.commonThemes'],
    },
    {
      id: 'negative_patterns',
      name: 'Negative patterns',
      description: 'Repeated complaints, gaps, or failure modes.',
      outputKeys: ['feedbackAnalysis.negativePatterns'],
    },
    {
      id: 'positive_patterns',
      name: 'Positive patterns',
      description: 'Repeated praise or successful interaction patterns.',
      outputKeys: ['feedbackAnalysis.positivePatterns'],
    },
  ],
};

// ── Description Quality Rubric v1 ───────────────────────────────────────────

export const DESCRIPTION_QUALITY_RUBRIC_V1: Rubric = {
  slug: 'description-quality-v1',
  name: 'Description Quality Rubric v1',
  version: 'v1',
  dimensions: [
    {
      id: 'description_clarity',
      name: 'Description clarity',
      description: 'Is the description specific, well-scoped, and free of jargon?',
      outputKeys: [
        'issues[].type=VAGUE_DESCRIPTION',
        'issues[].type=TECHNICAL_JARGON',
        'issues[].type=NARROW_SCOPE',
        'issues[].type=UNCLEAR_PURPOSE',
      ],
    },
    {
      id: 'keyword_match',
      name: 'Keyword match',
      description: 'Does the description match the natural language agents/users would use?',
      outputKeys: ['issues[].type=POOR_KEYWORD_MATCH'],
    },
    {
      id: 'trigger_definition',
      name: 'Trigger definition',
      description: 'Are skill triggers well-defined and discoverable?',
      outputKeys: ['issues[].type=MISSING_TRIGGERS'],
    },
    {
      id: 'agent_discoverability',
      name: 'Agent discoverability',
      description: 'Would an agent reading this description know when to invoke this skill?',
      outputKeys: ['suggestions[].type=DESCRIPTION_REWRITE'],
    },
  ],
};

// ── Lookup map ──────────────────────────────────────────────────────────────

/**
 * Read-only map of slug → Rubric. Frozen to prevent runtime mutation.
 *
 * Frontend lookup pattern (with unknown-slug fallback):
 *
 *   const rubric = RUBRICS[directive.rubricVersion ?? ''] ?? null;
 *   if (!rubric) {
 *     // render "legacy rubric" fallback
 *   }
 */
export const RUBRICS: Readonly<Record<string, Rubric>> = Object.freeze({
  [SELF_REVIEW_RUBRIC_V1.slug]: SELF_REVIEW_RUBRIC_V1,
  [DIRECTIVE_ANALYSIS_RUBRIC_V1.slug]: DIRECTIVE_ANALYSIS_RUBRIC_V1,
  [DESCRIPTION_QUALITY_RUBRIC_V1.slug]: DESCRIPTION_QUALITY_RUBRIC_V1,
});
